// Where a cafe item comes from. Keep the keys in sync with the `source` Literal
// on CreateCafeItemRequest in backend/app/models/cafe.py.
//
// "cafe" items are the partner-cafe menu — sold through the quantity dialog.
// The others are own stock: configured once in Settings, then sold with a single
// tap on the Cafe page so the caretaker never has to fill in a form.
// `color` is the chart/accent hex matching each badge, so dashboard charts and
// badges agree on what colour means what.
export const CAFE_SOURCES = {
  cafe: { label: "Cafe", badge: "bg-amber-500/15 text-amber-400", color: "#f59e0b" },
  fridge: { label: "Fridge", badge: "bg-sky-500/15 text-sky-400", color: "#0ea5e9" },
  cigarettes: { label: "Cigarettes", badge: "bg-orange-500/15 text-orange-400", color: "#f97316" },
};

// Own stock that is counted by the packet or bottle. The partner-cafe menu is
// cooked to order, so it is never counted. Mirrors STOCKED_SOURCES in
// backend/app/services/stock.py.
export const STOCKED_SOURCES = ["cigarettes", "fridge"];

// Legacy items predate the field; the API defaults them to "cafe" and this
// guards anything the API missed.
export function sourceOf(item) {
  return item?.source || "cafe";
}

/**
 * What one box of this is called. A drink bought singly is a bottle; bought 24
 * at a time it is a crate. Cigarettes are packets either way.
 */
export function boxNoun(config, packSize) {
  if (Number(packSize) > 1) return config?.packNounBox || "box";
  return config?.packNoun || "unit";
}

export function sourceMeta(source) {
  return CAFE_SOURCES[source] || CAFE_SOURCES.cafe;
}

// The one-tap sections, in the order they appear on the Cafe page and in
// Settings. Adding another kind of own-stock means adding one entry here plus
// the matching key in CAFE_SOURCES and the backend Literal.
export const QUICK_SELL_SOURCES = [
  {
    source: "cigarettes",
    title: "Cigarettes",
    // Category recorded on each item, so cafe reports can group by it.
    category: "Cigarettes",
    cafeHint: "Tap a pack once to record one sale.",
    settingsTitle: "Cigarette Items",
    settingsHint:
      "Add the brands you stock once. They appear as one-tap sale buttons on the Cafe page.",
    emptyCafe: "No cigarettes configured",
    emptySettings: "No cigarettes added yet.",
    addLabel: "Add a brand",
    namePlaceholder: "Name — e.g. Gold Flake King",
    addButton: "Add Cigarette",
    // Cigarettes are counted in packets, drinks in bottles.
    stockUnit: "packets",
    stockPlaceholder: "Packets",
    // One box of this is a packet of cigarettes; its size is per brand.
    packNoun: "packet",
    packNounBox: "packet",
    packSizeLabel: "Cigarettes per packet",
    accent: {
      icon: "text-orange-400",
      price: "text-orange-400",
      hover: "hover:border-orange-500/50 hover:bg-orange-500/5 active:bg-orange-500/15",
      button: "bg-orange-600 hover:bg-orange-700",
    },
  },
  {
    source: "fridge",
    title: "Refrigerator",
    category: "Cold Drinks",
    cafeHint: "Tap a drink once to record one sale.",
    settingsTitle: "Refrigerator Items",
    settingsHint:
      "Add your packaged drinks once. They appear as one-tap sale buttons on the Cafe page.",
    emptyCafe: "No drinks configured",
    emptySettings: "No drinks added yet.",
    addLabel: "Add a drink",
    namePlaceholder: "Name — e.g. Sprite 250ml",
    addButton: "Add Drink",
    stockUnit: "bottles",
    stockPlaceholder: "Bottles",
    packNoun: "bottle",
    packNounBox: "crate",
    packSizeLabel: "Bottles per crate",
    accent: {
      icon: "text-sky-400",
      price: "text-sky-400",
      hover: "hover:border-sky-500/50 hover:bg-sky-500/5 active:bg-sky-500/15",
      button: "bg-sky-600 hover:bg-sky-700",
    },
  },
];
